// client/src/intro-exit/GameIntroAndQuiz.jsx

/**
 * This file coordinates the player onboarding instruction guide and validation quiz layouts.
 * It dynamically embeds current treatment variable values and enforces strict verification criteria 
 * across a multi-stage flow before permitting players into active task streams.
 */

import React, { useState, useRef, useEffect } from "react";
import { useGame, usePlayer } from "@empirica/core/player/classic/react";

// ==================================================
// 1. Onboarding Guide & Verification Quiz Component
// ==================================================
export function GameIntroAndQuiz({ next }) {
  const [page, setPage] = useState(2); 
  const [quizAnswers, setQuizAnswers] = useState({
    q1: "", q2: "", q3: "", q4: "", q5: ""
  });
  const [quizError, setQuizError] = useState(false);

  // App.jsx wraps everything in `h-screen > h-full overflow-auto`, so the window
  // itself never scrolls -- that inner div does. window.scrollTo() is therefore a
  // no-op here, which is why advancing to the quiz left the page part-scrolled.
  // scrollIntoView walks up and scrolls whichever ancestor is actually scrollable.
  const topRef = useRef(null);
  const scrollToTop = (smooth = false) => {
    topRef.current?.scrollIntoView({
      block: "start",
      behavior: smooth ? "smooth" : "auto",
    });
  };

  // Run after the new page has rendered, not during the click handler.
  useEffect(() => {
    scrollToTop();
  }, [page]);

  const game = useGame();
  const player = usePlayer();

  // Read dynamic bonus factor from active treatment parameters
  const bonusFactor = game?.get("treatment")?.correctPredictionBonus !== undefined
    ? parseFloat(game.get("treatment").correctPredictionBonus)
    : 0.5; 

  // Read each round count only when the treatment actually omits it. The earlier
  // form tested truthiness, so a configured 0 -- a legitimate value for either
  // post-rewire block -- fell through to the default and the participant was told
  // about rounds the game would never run. Defaults match callbacks.js, so the
  // round totals quoted here cannot disagree with the game that gets built.
  const treatmentValue = (key, fallback) => {
    const raw = game?.get("treatment")?.[key];
    if (raw === undefined || raw === null || raw === "") return fallback;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? fallback : n;
  };

  const initialPhase = treatmentValue("initialPhaseRounds", 4);
  const postRewireEasy = treatmentValue("postRewireEasyRounds", 1);
  const postRewireHard = treatmentValue("postRewireHardRounds", 1);
  const totalRealRounds = initialPhase + postRewireEasy + postRewireHard;

  // The round-structure question contrasts "1 practice + N evaluative" against
  // "N practice + 1 evaluative". When N is 1 those two options describe the same
  // thing, so the question has no single correct answer and is dropped entirely.
  const showRoundStructureQuestion = totalRealRounds > 1;

  // The correct option is deliberately NOT always the same position -- A, B, A, C,
  // C -- so the quiz cannot be passed by clicking straight down one column without
  // reading.
  // q4 is only required when its question is actually shown -- otherwise it could
  // never be answered and the quiz would be impossible to pass.
  const correctAnswers = {
    q1: "opt_a",
    q2: "opt_b",
    q3: "opt_a",
    ...(showRoundStructureQuestion ? { q4: "opt_c" } : {}),
    q5: "opt_c"
  };

  // ==========================================
  // 2. Quiz Validation Handlers
  // ==========================================
  const handleValidationSubmit = (e) => {
    e.preventDefault();
    const isAllCorrect = Object.keys(correctAnswers).every(
      (key) => quizAnswers[key] === correctAnswers[key]
    );

    if (isAllCorrect) {
      if (player && player.get("quizFailedAttempts") === undefined) {
        player.set("quizFailedAttempts", 0);
      }
      setQuizError(false);
      next(); 
    } else {
      // Count failed attempts as a comprehension measure. Written straight to the
      // player so it survives into the export rather than living in local state.
      const failures = (player?.get("quizFailedAttempts") || 0) + 1;
      player?.set("quizFailedAttempts", failures);

      setQuizError(true);
      scrollToTop(true);
    }
  };

  // ==========================================
  // 3. Main Return Component Render
  // ==========================================
  return (
    <div ref={topRef} className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-sans antialiased text-gray-900">
      <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl border border-gray-200 mx-auto overflow-hidden">
        
        {/* Onboarding Wizard Header Tracker */}
        <div className="bg-gray-900 px-6 py-4 flex items-center justify-between text-white">
          <div>
            <h1 className="app-page-title">Loan Risk Assessment Experiment</h1>
            <p className="app-page-subtitle text-gray-400 mt-0.5">Please complete the setup documentation steps in order.</p>
          </div>
          <div className="app-micro-label flex items-center gap-2 font-mono bg-gray-800 px-3 py-1.5 rounded-xl border border-gray-700">
            <span className="text-gray-400">1. Agreement</span>
            <span className="text-gray-600">&bull;</span>
            <span className={page === 2 ? "text-emerald-400" : "text-gray-500"}>2. Instructions</span>
            <span className="text-gray-600">&bull;</span>
            <span className={page === 3 ? "text-emerald-400" : "text-gray-500"}>3. Quiz</span>
          </div>
        </div>

        <div className="p-8 sm:p-10 space-y-6">
          {/* =========================================================
              4. PAGE 2: EXPERIMENT GUIDE VIEW
              ========================================================= */}
          {page === 2 && (
            <div className="app-body space-y-6 animate-fadeIn text-gray-600">

              <div className="border-b pb-3">
                <h2 className="app-section-title text-gray-800">Experiment Guide</h2>
                <p className="app-section-subtitle text-gray-500 mt-0.5">Learn how to play the game, read borrower information, and earn bonus points.</p>
              </div>

              {/* SECTION 1: OBJECTIVE */}
              <div className="space-y-2">
                <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
                  1. Objective & Setup
                </h3>
                <p className="app-body">
                  In this game, you will act as a loan analyst. Your goal is to <strong>Predict Default</strong> if you believe the borrower will fail to pay 
                  back the loan, or <strong>Predict Repaid</strong> if you believe they will pay it back. The game consists of <strong>1 practice round</strong> followed by  <strong>{initialPhase 
                  + postRewireEasy + postRewireHard} separate loan rounds total</strong>.
                </p>
              </div>

              {/* SECTION 1b: WHAT YOU SEE ABOUT EACH BORROWER
                  This page is shown before roles are assigned, so it can only talk
                  about public and private features in general terms -- the specific
                  feature a participant holds is named later, on the variable
                  introduction screen. What it has to establish here is that a
                  private feature exists at all and that its usefulness is not fixed,
                  because the quiz question below tests exactly that and because a
                  participant who assumes the public numbers are the whole picture
                  has no reason to weigh their own signal against them. */}
              <div className="space-y-2">
                <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
                  2. What You Will See About Each Borrower
                </h3>
                <p className="app-body">
                  For every borrower you will see a set of <strong>public features</strong> that
                  all players see, plus <strong>one private feature that only you can see</strong>.
                  Every player is given a private feature, but not the same one — the other
                  players each hold a private feature of their own, and they cannot see yours.
                </p>
                <p className="app-body">
                  Private features are not all equally useful. Depending on which one you are
                  given, <strong>your private feature may be more helpful or less helpful than
                  the public features</strong> for predicting whether a borrower defaults. Before
                  the first round you will be shown exactly how strongly each of your own
                  variables is related to default, so you can judge how much weight to give it.
                </p>
              </div>

              {/* SECTION 2: PROCESS OF THE GAME */}
              <div className="space-y-2">
                <h3 className="app-subsection-title text-gray-900 border-l-2 border-gray-900 pl-2">
                  3. Process of a Round (3 Steps)
                </h3>
                <p className="app-body">
                  Every single round runs through these 3 steps in order:
                </p>
                <div className="app-body grid grid-cols-1 md:grid-cols-3 gap-3 text-gray-700 pt-1">
                  <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <span className="text-indigo-600 font-bold block mb-0.5">Step 1: First Prediction</span>
                    Look at the borrower's numbers and submit your own First Prediction independently.
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <span className="text-indigo-600 font-bold block mb-0.5">Step 2: Advisors & Second Prediction</span>
                    See what your assigned Advisors (up to 2) guessed in Step 1. Use their predictions to help update and lock in your own Second Prediction.
                  </div>
                  <div className="p-3 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <span className="text-indigo-600 font-bold block mb-0.5">Step 3: Results & Compensation</span>
                    The true outcome is revealed. You earn <span className="text-emerald-600 font-bold font-mono">+${bonusFactor.toFixed(2)}</span> if your First Prediction was correct, and another <span className="text-emerald-600 font-bold font-mono">+${bonusFactor.toFixed(2)}</span> if your Second Prediction was correct!
                  </div>
                </div>
              </div>

              {/* SPECIAL EVENT: NETWORK REWIRING (ROUND 5) */}
              <div className="space-y-2 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                <h3 className="app-subsection-title text-blue-900 flex items-center gap-1.5">
                  Advisor Changes (Round {initialPhase + 1})
                </h3>
                <p className="app-body text-blue-950">
                  Right before <strong>Round {initialPhase + 1} starts</strong>, you will have an <strong> opportunity to change or keep your advisors </strong> who could serve as alternative advisors moving forward.
                </p>
                <p className="app-body text-blue-900/80">
                  You will see a menu showing your current Advisors alongside brand-new alternative advisors. This screen will show each advisor's tracked historical accuracy in their "First Prediction" from the first {initialPhase} rounds. You will have the option to keep your current Advisors or swap them out for better-performing advisors to maximize your advice quality for the final rounds!
                </p>
              </div>

              {/* NAVIGATION ACTION FOOTER */}
              <div className="pt-4 border-t flex justify-end">
                <button 
                  onClick={() => {
                    setPage(3);
                  }} 
                  className="app-btn px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition shadow-md"
                >
                  Continue to Verification Quiz
                </button>
              </div>
            </div>
          )}

          {/* =========================================================
              5. PAGE 3: 5-QUESTION QUIZ WITH RADIO BUTTONS
              ========================================================= */}
          {page === 3 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="border-b pb-3">
                <h2 className="app-section-title text-gray-800">Verification Quiz</h2>
                <p className="app-section-subtitle text-gray-500 mt-0.5">Answer all 5 questions correctly to start the evaluation round stages.</p>
              </div>

              {quizError && (
                <div className="app-error p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl animate-pulse">
                  ✕ Verification Failed: Some responses are incorrect. Please review the instructions and try again.
                </div>
              )}

              <form onSubmit={handleValidationSubmit} className="app-body space-y-6 text-gray-700">
                
                {/* Q1 -- bonus structure. The separate "which predictions earn a
                    bonus?" item this replaced asked the same thing twice: both
                    distractors here now carry that information, since each names
                    which single prediction the smaller payout would come from. */}
                <div className="space-y-2">
                  <label className="app-question block text-gray-800">
                    1. What is the maximum number of bonus points you can earn in a single round?
                  </label>
                  <div className="flex flex-col gap-2">
                    {[
                      { val: "opt_a", text: `A) $${(bonusFactor * 2).toFixed(2)} total (+$${bonusFactor.toFixed(2)} for First prediction correct, +$${bonusFactor.toFixed(2)} for Second prediction correct)` },
                      { val: "opt_b", text: `B) $${bonusFactor.toFixed(2)} total (only from a correct Second Prediction)` },
                      { val: "opt_c", text: `C) $${bonusFactor.toFixed(2)} total (only from a correct First Prediction)` }
                    ].map((opt) => (
                      <label key={opt.val} className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${quizAnswers.q1 === opt.val ? "bg-blue-50 border-blue-400 font-medium text-blue-900" : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"}`}>
                        <input
                          type="radio"
                          name="q1"
                          value={opt.val}
                          checked={quizAnswers.q1 === opt.val}
                          onChange={(e) => setQuizAnswers({ ...quizAnswers, q1: e.target.value })}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span>{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Q2 -- the private signal. Phrased without naming a specific
                    feature because roles are assigned at game start, after this
                    page. What it has to rule out is the two ways a participant can
                    get the setup wrong: assuming everyone sees the same numbers,
                    and assuming a private feature is by definition the weaker
                    evidence. Getting the second one wrong is what makes a strong
                    private signal get discarded on the cases that turn on it. */}
                <div className="space-y-2">
                  <label className="app-question block text-gray-800">
                    2. Which statement about the borrower information is correct?
                  </label>
                  <div className="flex flex-col gap-2">
                    {[
                      { val: "opt_a", text: "A) Every player sees exactly the same information about each borrower" },
                      { val: "opt_b", text: "B) Along with the public features, you see one private feature unique to you, and it may be more helpful or less helpful than the public features for predicting default" },
                      { val: "opt_c", text: "C) Along with the public features, you see one private feature unique to you, and it is always less helpful than the public features" }
                    ].map((opt) => (
                      <label key={opt.val} className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${quizAnswers.q2 === opt.val ? "bg-blue-50 border-blue-400 font-medium text-blue-900" : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"}`}>
                        <input
                          type="radio"
                          name="q2"
                          value={opt.val}
                          checked={quizAnswers.q2 === opt.val}
                          onChange={(e) => setQuizAnswers({ ...quizAnswers, q2: e.target.value })}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span>{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Q3 */}
                <div className="space-y-2">
                  {(() => {
                    return (
                      <>
                        <label className="app-question block text-gray-800">
                          3. On what basis should you evaluate alternative advisor options during Round {initialPhase + 1}?
                        </label>
                        <div className="flex flex-col gap-2">
                          {[
                            { val: "opt_a", text: `A) By tracking their First Prediction accuracy from the first ${initialPhase} rounds` },
                            { val: "opt_b", text: "B) By looking at their anonymous panel tracking names" },
                            { val: "opt_c", text: "C) Completely at random" }
                          ].map((opt) => (
                            <label key={opt.val} className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${quizAnswers.q3 === opt.val ? "bg-blue-50 border-blue-400 font-medium text-blue-900" : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"}`}>
                              <input
                                type="radio"
                                name="q3"
                                value={opt.val}
                                checked={quizAnswers.q3 === opt.val}
                                onChange={(e) => setQuizAnswers({ ...quizAnswers, q3: e.target.value })}
                                className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              />
                              <span>{opt.text}</span>
                            </label>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Q4 -- only shown when the two options are actually distinct */}
                {showRoundStructureQuestion && (
                <div className="space-y-2">
                  <label className="app-question block text-gray-800">
                    4. Which of the following describes the rounds you will complete in this study?
                  </label>
                  <div className="flex flex-col gap-2">
                    {(() => {

                      return [
                        { val: "opt_a", text: `A) ${totalRealRounds + 1} evaluative rounds, with no practice round` },
                        { val: "opt_b", text: `B) ${totalRealRounds} initial Practice Rounds, then 1 Evaluative Round` },
                        { val: "opt_c", text: `C) 1 initial Practice Round, then ${totalRealRounds} Evaluative Rounds` }
                      ].map((opt) => (
                        <label key={opt.val} className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${quizAnswers.q4 === opt.val ? "bg-blue-50 border-blue-400 font-medium text-blue-900" : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"}`}>
                          <input
                            type="radio"
                            name="q4"
                            value={opt.val}
                            checked={quizAnswers.q4 === opt.val}
                            onChange={(e) => setQuizAnswers({ ...quizAnswers, q4: e.target.value })}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <span>{opt.text}</span>
                        </label>
                      ));
                    })()}
                  </div>
                </div>
                )}

                {/* Q5 */}
                <div className="space-y-2">
                  <label className="app-question block text-gray-800">
                    {showRoundStructureQuestion ? "5" : "4"}. After seeing your advisors' predictions, what can you do with your First Prediction?
                  </label>
                  <div className="flex flex-col gap-2">
                    {[
                      { val: "opt_a", text: "A) Keep it, even if you now believe another option is more likely correct" },
                      { val: "opt_b", text: "B) Change it to your advisors' option, even if you believe they are wrong" },
                      { val: "opt_c", text: "C) Keep it, or change it to whichever option you believe is most likely correct" }
                    ].map((opt) => (
                      <label key={opt.val} className={`app-choice p-3 border rounded-xl flex items-center gap-3 cursor-pointer transition-colors ${quizAnswers.q5 === opt.val ? "bg-blue-50 border-blue-400 font-medium text-blue-900" : "bg-gray-50 border-gray-200 hover:bg-gray-100/70"}`}>
                        <input
                          type="radio"
                          name="q5"
                          value={opt.val}
                          checked={quizAnswers.q5 === opt.val}
                          onChange={(e) => setQuizAnswers({ ...quizAnswers, q5: e.target.value })}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <span>{opt.text}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Action Form Footer Layout */}
                <div className="pt-6 border-t flex justify-between items-center">
                  <button 
                    type="button"
                    onClick={() => {
                      setPage(2);
                    }} 
                    className="app-btn px-5 py-2 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                  >
                    Back to Instructions
                  </button>
                  <button 
                    type="submit" 
                    className="app-btn px-10 py-2.5 bg-black text-white rounded-xl hover:bg-emerald-500 transition shadow-md"
                  >
                    Submit & Verify
                  </button>
                </div>
              </form>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
